import { useContext, useMemo, useState } from 'react';
import { Button, Spinner, useDisclosure, Pagination } from '@ds';
import { renderPaginationItem } from '@ui';
import { AuthContext } from '@lp/core/auth';
import { useMultiSuggestionItemReviewsQuery } from '@/queries/multi-suggest';
import { SuggestionReviewType } from '@/api/apis/multi-suggest';
import { ListEmptyState } from '../ListEmptyState';
import { MultiSuggestReview } from './MultiSuggestReview';
import { MultiSuggestReviewModal } from './MultiSuggestReviewModal';

const MAX_REVIEWS_PER_PAGE = 5;

type MultiSuggestReviewsListProps = {
  taskId: string;
};

export const MultiSuggestReviewsList = ({ taskId }: MultiSuggestReviewsListProps) => {
  const { profile } = useContext(AuthContext);
  const { data, isLoading } = useMultiSuggestionItemReviewsQuery(taskId);
  const [currentPage, setCurrentPage] = useState(1);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedReview, setSelectedReview] = useState<SuggestionReviewType | null>(null);

  const reviewsCount = data?.length || 0;
  const totalPages = Math.ceil(reviewsCount / MAX_REVIEWS_PER_PAGE) || 1;

  const paginatedReviewsList = useMemo(() => {
    if (!data?.length) return [];

    const startIndex = (currentPage - 1) * MAX_REVIEWS_PER_PAGE;
    const endIndex = startIndex + MAX_REVIEWS_PER_PAGE;

    return data.slice(startIndex, endIndex);
  }, [data, currentPage]);

  const handleEditReview = (review: SuggestionReviewType) => {
    setSelectedReview(review);
    onOpen();
  };

  const handleModalClose = () => {
    setSelectedReview(null);
    onClose();
  };

  const handleOpenNewReviewModal = () => {
    setSelectedReview(null);
    onOpen();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col grow place-items-stretch gap-4 p-6 bg-base-70">
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <Spinner size="md" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full flex flex-col items-stretch gap-4 py-4 pr-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-1 items-center">
            <span className="text-hm text-base-20">Reviews</span>
            <span className="text-hs text-base-30">{`(${reviewsCount})`}</span>
          </div>
          <Button size="md" variant="outline" onPress={handleOpenNewReviewModal}>
            Add Review
          </Button>
        </div>
        {paginatedReviewsList ? (
          <div className="flex gap-4">
            {paginatedReviewsList.map((review) => (
              <MultiSuggestReview
                key={review.id}
                data={review}
                onEdit={handleEditReview}
                isReadOnly={profile?.userId !== review.user.userId}
              />
            ))}
          </div>
        ) : (
          <ListEmptyState message="There are no reviews found." />
        )}
        <div className="flex items-center justify-center">
          <Pagination
            size="sm"
            showControls
            page={currentPage}
            onChange={setCurrentPage}
            total={totalPages}
            renderItem={renderPaginationItem}
            classNames={{
              wrapper: 'gap-2',
              cursor: 'text-s-sb! rounded-small bg-black text-white',
            }}
          />
        </div>
      </div>
      {isOpen && (
        <MultiSuggestReviewModal
          isOpen={isOpen}
          onCloseModal={handleModalClose}
          suggestionId={taskId}
          review={selectedReview}
        />
      )}
    </>
  );
};
